import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, useToast } from "@aviary";
import { useBreakpoints } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import { SignInWrapper, ErrorBox } from "@unauthenticated/shared/components";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { ForgotPasswordForm, ReturnToSignInButton } from "../";

import { PasswordSentPage } from "./PasswordSentPage";
import { useUserForgotPasswordEmail } from "./UserForgotPasswordEmail.mutation";

import * as styles from "./ForgotPasswordContent.styles";

const ForgotPasswordContent: FC = () => {
  const { tablet } = useBreakpoints();
  const search = useLocation().search;
  const { makeToast } = useToast();
  const urlParams = new URLSearchParams(search);
  const urlEmail = urlParams.get("email");
  const error = new URLSearchParams(search).get("error");

  const [email, setEmail] = useState(urlEmail || "");

  const { t } = useTranslation();

  const [requestNewPassword, { data, loading }] = useUserForgotPasswordEmail({
    onError: ({ message }) => {
      makeToast("error", message);
    },
  });

  const onSubmit = e => {
    e.preventDefault();
    gRecaptchaExecute(captchaToken =>
      requestNewPassword({
        variables: {
          input: {
            captchaToken,
            attributes: {
              email,
            },
          },
        },
      })
    );
  };

  const isEmailSent = data?.auth?.userForgotPasswordEmail?.emailSent;

  const renderEmailSentContent = <PasswordSentPage email={email} />;

  const renderForgotPasswordForm = (
    <div css={styles.container}>
      <SignInWrapper>
        <Typography type={tablet.greaterThan ? "h2" : "h3"}>{t(l.signIn.ResetPassword)}</Typography>
        {error && (
          <ErrorBox
            error={t(l.signIn.LinkIsBroken)}
            subMessage={t(l.signIn.TryAgainResetPassword)}
          />
        )}
        <Typography>{t(l.signIn.HappensToTheBest)}</Typography>
        <ForgotPasswordForm
          onSubmit={onSubmit}
          isLoading={loading}
          setEmail={setEmail}
          email={email}
        />
        <ReturnToSignInButton />
      </SignInWrapper>
    </div>
  );

  return isEmailSent ? renderEmailSentContent : renderForgotPasswordForm;
};

export { ForgotPasswordContent };
